<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="ユーザ情報"
  >
    <template v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  v-if="user.loginId === $store.state.user.loginUser.loginId"
                  color="primary"
                  @click="passwordModify"
                  >パスワード変更
                </v-btn>
                <v-btn
                  v-permission.admin
                  v-if="user.loginId !== $store.state.user.loginUser.loginId"
                  color="primary"
                  @click="oneTimeUrlIssue"
                  class="ml-2"
                  >パスワード再発行
                </v-btn>
                <v-btn
                  v-if="showModify"
                  color="primary"
                  @click="showUserModify"
                  class="ml-2"
                  >変更</v-btn
                >
                <v-btn
                  v-if="showDelete"
                  color="primary"
                  @click="del"
                  class="ml-2"
                  >削除</v-btn
                >
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-right"
                >最終更新日：{{ $filter.date(user.updateDate) }}</v-col
              >
            </v-row>
            <v-row>
              <v-col cols="12">
                <app-label-group label="ログインID" :value="user.loginId" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group label="姓" :value="user.familyName" />
              </v-col>
              <v-col cols="6">
                <app-label-group label="名" :value="user.givenName" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="姓(カナ)"
                  :value="$filter.emptyTo(user.familyNameKana, '-')"
                />
              </v-col>
              <v-col cols="6">
                <app-label-group
                  label="名(カナ)"
                  :value="$filter.emptyTo(user.givenNameKana, '-')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="IPアドレス認証"
                  :value="user.ipAuthAddressList.length !== 0 ? '有' : '無'"
                />
              </v-col>
            </v-row>
            <v-row v-if="user.ipAuthAddressList.length > 0">
              <v-col cols="6">
                <app-label-group label="認証IPアドレス">
                  <template #value>
                    <div class="ml-3">
                      <p
                        class="m-0 mb-1"
                        v-for="(e, index) in sortedIpAddresses"
                        :key="index"
                      >
                        {{ e }}
                      </p>
                    </div>
                  </template>
                </app-label-group>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="所属グループ"
                  :value="$filter.emptyTo(user.group, '-')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="連絡先電話番号"
                  :value="$filter.emptyTo(user.phone, '-')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="連絡先メールアドレス"
                  :value="$filter.emptyTo(user.email, '-')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="登録日"
                  :value="$filter.date(user.createDate)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <app-label-group
                  label="運用種別"
                  :value="
                    $filter.optionText(user.operationType, operationTypeOption)
                  "
                />
              </v-col>
            </v-row>
            <v-row v-if="loginUser && loginUser.loginId === user.loginId">
              <v-col cols="6">
                <app-label-group
                  label="最終ログイン日時"
                  :value="$filter.datetime(loginUser.loginDateTime)"
                />
              </v-col>
              <v-col cols="6">
                <app-label-group
                  label="最終ログアウト日時"
                  :value="$filter.datetime(loginUser.logoutDateTime)"
                />
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { UserEntity } from "@/apis/UserApi";
import { VueFinalModal } from "vue-final-modal";
import OneTimeUrlIssueModal from "@/modals/password/OneTimeUrlIssueModal.vue";
import UserEdit from "@/modals/user/UserEdit.vue";
import {
  ROLE_CONFIG_USER,
  ROLE_DELEGATE_VIEW_USER,
  ROLE_VIEW_USER,
  type LoginUser,
} from "@/apis/InformationApi";
import PasswordModify from "@/modals/password/PasswordModify.vue";

export default defineComponent({
  name: "UserReference",
  props: {
    /** ユーザ設定情報 */
    user: {
      type: Object as PropType<UserEntity>,
      required: true,
    },
    /** ユーザー管理排他情報 */
    userKeyId: {
      type: String as PropType<string>,
      required: true,
    },
    /** ログインユーザー情報 */
    loginUser: {
      type: Object as PropType<LoginUser>,
      required: true,
    },
  },
  data() {
    return {
      operationTypeOption: [
        { text: "法人管理者", value: "ADMIN" },
        { text: "設定権限", value: "SETTING" },
        { text: "閲覧権限", value: "VIEWING" },
      ],
    };
  },
  computed: {
    /**
     * true: 変更可能, false: 変更不可
     * 法人の場合は自身以下の権限のユーザーのみ可能
     * 閲覧権限代行権限、閲覧権限は不可、その他は可能
     */
    showModify(): boolean {
      const role = this.$store.state.user.loginUser.roleCode;
      if (role === ROLE_DELEGATE_VIEW_USER || role === ROLE_VIEW_USER) {
        return false;
      }
      if (role === ROLE_CONFIG_USER && this.user.operationType === "ADMIN") {
        return false;
      }
      return true;
    },
    /**
     * true: 削除可能, false: 削除不可
     * 変更可能 + 自身でないこと
     */
    showDelete(): boolean {
      return (
        this.showModify &&
        this.user.loginId !== this.$store.state.user.loginUser.loginId
      );
    },
    /**
     * IPアドレスを数値順にソートして返す
     */
    sortedIpAddresses(): string[] {
      return this.user.ipAuthAddressList.sortIpAddress();
    },
  },
  methods: {
    /** パスワード変更 */
    async passwordModify() {
      await this.$modal.show(PasswordModify);
      await (
        this.$refs.modal as typeof VueFinalModal & { ok: () => void }
      ).ok();
    },
    /** パスワード再発行 */
    async oneTimeUrlIssue() {
      await this.$modal.show(OneTimeUrlIssueModal);
    },
    /** ユーザ変更 */
    async showUserModify() {
      await this.$modal.show(UserEdit, {
        userData: this.user,
        userKeyId: this.userKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** ユーザ情報削除 */
    async del() {
      await this.$confirm(`ユーザを削除します。よろしいですか？`);
      await this.$api.user.delete({
        userSeqList: [this.user.userSeq],
        userKeyId: this.userKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
